import { useState, useMemo, memo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { CalendarEventCard } from "./CalendarEventCard";
import { format, isSameDay } from "date-fns";
import { DayContentProps } from "react-day-picker";
import { getCentralTimeDate } from "@/utils/timezone";

// Helper to get a comparable date from Central Time
const getCentralComparableDate = (isoDate: string): Date => {
  const centralDate = getCentralTimeDate(isoDate);
  return new Date(
    centralDate.getUTCFullYear(),
    centralDate.getUTCMonth(),
    centralDate.getUTCDate()
  );
};

interface Event {
  id: string;
  title: string;
  date: string;
  location: string;
  category: string;
  host_name: string;
  duration_min?: number;
  description?: string;
  time_of_day?: string;
}

interface EventCalendarProps {
  events: Event[];
  userRsvps: string[];
  onRsvp: (eventId: string) => void;
}

export const EventCalendar = memo(function EventCalendar({ events, userRsvps, onRsvp }: EventCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  // Memoize parsed event dates in Central Time to avoid re-parsing on every render
  const eventDates = useMemo(() => events.map(event => getCentralComparableDate(event.date)), [events]);

  // Get events for selected date (compare in Central Time)
  const selectedEvents = useMemo(() => {
    if (!selectedDate) return [];
    return events.filter(event => isSameDay(getCentralComparableDate(event.date), selectedDate));
  }, [events, selectedDate]);

  // Next day (strictly after the selected day) that actually has events
  const nextEventDay = useMemo(() => {
    if (!selectedDate) return null;
    const upcoming = events
      .map(event => getCentralComparableDate(event.date))
      .filter(date => date.getTime() > selectedDate.getTime())
      .sort((a, b) => a.getTime() - b.getTime());
    return upcoming[0] ?? null;
  }, [events, selectedDate]);

  const nextEventDayCount = useMemo(() => {
    if (!nextEventDay) return 0;
    return events.filter(event => isSameDay(getCentralComparableDate(event.date), nextEventDay)).length;
  }, [events, nextEventDay]);

  // Check if a date has events (in Central Time)
  const hasEventsOnDate = (date: Date) => {
    return eventDates.some(eventDate => isSameDay(eventDate, date));
  };

  const eventCountOnDate = (date: Date) => {
    return eventDates.filter(eventDate => isSameDay(eventDate, date)).length;
  };

  // Check if a date has RSVP'd events (in Central Time)
  const hasRsvpOnDate = (date: Date) => {
    return events.some(event => 
      isSameDay(getCentralComparableDate(event.date), date) && userRsvps.includes(event.id)
    );
  };

  // Custom day content renderer with indicator dots (one per event, max 3)
  const renderDayContent = (date: Date) => {
    const count = eventCountOnDate(date);
    const hasRsvp = hasRsvpOnDate(date);

    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <span>{date.getDate()}</span>
        {count > 0 && (
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
            {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
              <div
                key={i}
                className={`w-1 h-1 rounded-full ${hasRsvp ? 'bg-primary' : 'bg-muted-foreground'}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };


  // Custom modifiers for styling (using Central Time dates)
  const modifiers = {
    hasRsvps: eventDates.filter(date => 
      events.some(event => 
        isSameDay(getCentralComparableDate(event.date), date) && userRsvps.includes(event.id)
      )
    ),
  };

  const modifiersStyles = {
    hasRsvps: {
      fontWeight: 'bold' as const,
    },
  };

  return (
    <div className="space-y-4 px-6">
      <Card className="p-4">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          className="rounded-md pointer-events-auto"
          components={{
            DayContent: ({ date }: DayContentProps) => renderDayContent(date)
          }}
        />
      </Card>

      {selectedDate && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">
            Events on {format(selectedDate, 'MMMM d, yyyy')}
          </h3>
          {selectedEvents.length === 0 ? (
            <Card className="p-6 text-center">
              <p className="text-muted-foreground">
                No events scheduled for this day
              </p>

              {nextEventDay ? (
                <button
                  onClick={() => setSelectedDate(nextEventDay)}
                  className="mt-3 min-h-[44px] text-sm font-semibold text-primary"
                >
                  Next event: {format(nextEventDay, 'EEEE, MMMM d')}
                  {nextEventDayCount > 1 ? ` (${nextEventDayCount} events)` : ''}
                </button>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  No upcoming events on the calendar yet.
                </p>
              )}
            </Card>
          ) : (

            <div className="space-y-3">
              {selectedEvents.map(event => (
                <CalendarEventCard
                  key={event.id}
                  event={event}
                  isRsvpd={userRsvps.includes(event.id)}
                  onRsvp={() => onRsvp(event.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
